import { Inject, Injectable, Renderer2 } from '@angular/core';
import { DOCUMENT, Location } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

import { environment } from 'src/environments/environment';
import { LanguageKey } from '../enums/language.enum';
import { Language } from '../models/language.model';

@Injectable()
export class LanguageService {
    private isOneLinkLoaded: boolean = false;
    private currentLanguage!: LanguageKey;
    private languages: Language[] = [
        {
            label: 'English',
            key: LanguageKey.En,
            routeParam: ''
        },
        {
            label: 'Español',
            key: LanguageKey.Es,
            routeParam: 'es'
        },
    ];
    private get window(): Window {
        return this.document.defaultView;
    }

    currentLanguageSubscription: BehaviorSubject<LanguageKey>;

    constructor(
        private location: Location,
        @Inject(DOCUMENT) private document: any,
    ) {
        // set default language
        this.currentLanguage = environment.multilingual.defaultLang as LanguageKey;
        this.currentLanguageSubscription = new BehaviorSubject<LanguageKey>(this.currentLanguage);
    }

    isCurrentLanguage(langKey: LanguageKey) {
        return this.currentLanguage === langKey ? true : false;
    }

    isDefaultLanguage(langKey: LanguageKey): boolean {
        return langKey === (environment.multilingual.defaultLang as LanguageKey);
    }

    getCurrentLanguage(): LanguageKey {
        return this.currentLanguage;
    }

    /** if input is not defined, set a default value */
    setCurrentLanguage(langKey?: LanguageKey) {
        this.currentLanguage = langKey || environment.multilingual.defaultLang as LanguageKey;
        this.currentLanguageSubscription.next(this.currentLanguage);
    }

    /** e.g. EN => English */
    getLanguageLabel(langKey: LanguageKey): string {
        const lang = this.languages.find(l => l.key === langKey);
        return lang?.label || '';
    }

    /** 
     * e.g. EN => en 
     * if input not defined, will return router param for current language
     */
    getLanguageRouteParam(langKey?: LanguageKey): string | undefined {
        const lang = this.languages.find(l => l.key === (langKey || this.currentLanguage));
        return lang?.routeParam;
    }

    getLanguageKeyByRouteParam(langParam: string | undefined): LanguageKey | undefined {
        if (!langParam) {
            return LanguageKey.En;
        }
        const lang = this.languages.find(l => l.routeParam === langParam);
        return lang?.key;
    }

    updateCurrentUrlWithLanguageParam(langKey: LanguageKey) {
        // use window location object instead of angular this.location.path() because the last one skips traling slash
        const url = this.replaceLangInUrl(this.window.location.pathname, langKey, true);
        this.location.replaceState(url);
    }

    replaceLangInUrl(url: string, langKey: LanguageKey, replaceForAnyUrl = false): string {
        try {
            let urlChunks = url.split('/');
            const isDevEnv = url.includes('localhost');
            const isRelativeUrl = url.startsWith('/');
            const isJayrideDomain = urlChunks[2] ? urlChunks[2].includes('jayride.com') : false;

            if (replaceForAnyUrl || isDevEnv || isRelativeUrl || isJayrideDomain) {
                urlChunks = this.replaceFragmentInUrl(
                    urlChunks,
                    isRelativeUrl ? 1 : 3,
                    langKey
                );
                return urlChunks.join('/');
            }
        } catch (err) {
            console.error(err);
        }
        return url;
    }

    private replaceFragmentInUrl(urlChunks: string[], i: number, langKey: LanguageKey): string[] {
        const currentLangParam = this.languages.find(l => l.routeParam === urlChunks[i]);
        const newLangParam = this.getLanguageRouteParam(langKey);
        if (currentLangParam) {
            // update existing language route parameter
            if (newLangParam) {
                // if new url param is defined - replace the old one with the new one 
                urlChunks[i] = newLangParam;
            } else {
                // new language param is empty, so remove that chunk from url 
                urlChunks.splice(i, 1);
            }
        } else {
            // insert language param as it does not exists in the current url
            if (newLangParam) {
                urlChunks.splice(i, 0, newLangParam);
            }
        }

        return urlChunks;
    }

    updateAllLinksOnPage(lang: LanguageKey) {
        let aEls = document.querySelectorAll('a');
        aEls.forEach(aEl => {
            if (aEl.href) {
                aEl.href = this.replaceLangInUrl(aEl.href, lang);
            }
        });
    }

    getCurrentLanguageFromUrl(): LanguageKey {
        // use window location object instead of angular this.location.path() because the last one skips traling slash
        const urlChunks = this.window.location.pathname.split('/');
        const lang = this.languages.find(l => l.routeParam === urlChunks[1]);
        return lang?.key || this.currentLanguage;
    }

    loadOneLinkScript(renderer: Renderer2) {
        if (!this.isOneLinkLoaded) {
            const script = renderer.createElement('script');
            script.type = 'text/javascript';
            script.src = `https://www.onelink-edge.com/moxie.min.js`;
            script.setAttribute('data-oljs', environment.multilingual.oneLinkApiKey);
            renderer.appendChild(document.head, script);
            this.isOneLinkLoaded = true;
        } else {
            console.log('OneLink script is already loaded.');
        }
    }
}